/* eslint-disable no-param-reassign */

import * as React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router';
import { HashLoader } from 'react-spinners';
import { Dispatch } from 'redux';
import { clearSubmitErrors, Form, InjectedFormProps, reduxForm, startSubmit, SubmissionError } from 'redux-form';

import { createFormSubmitAction } from '../../actions/formActions';
import { DataRequestActionTypes } from '../../constants/actionTypes';
import { FormId } from '../../constants/formId';
import * as FormHelper from '../../helpers/formHelper';
import { ClippingPlatformState } from '../../store/clippingPlatformState';
import {
  baseMapDispatchToProps,
  baseMapStateToProps,
  ComponentBase,
  BaseDispatchProps,
  BaseStateProps,
} from '../componentBase';

import styles from './appForm.module.scss';

export interface AppFormOwnProps<FormData> extends Partial<InjectedFormProps<FormData>> {
  form: FormId;
  hideButtons?: boolean;
  preprocessDataOnSubmit?: (formData: any) => any;
  submitText?: string;
  submitActionType: DataRequestActionTypes;
  successMessage?: string;
  reduxData?: any;
  redirectToPathAfterSubmit?: string;
  className?: string;
  title?: string;
  onSuccess?: () => void;
  cancelText?: string;
  onCancel?: () => void;
  disableSubmit?: boolean;
}

export interface AppFormStateProps extends BaseStateProps {
  error: string;
  submitting: boolean;
  submittedSuccessfully: boolean;
  submitErrors: any;
  submitFailed: boolean;
}

export interface AppFormDispatchProps extends BaseDispatchProps {
  clearFormErrors: () => void;
}

export class AppForm extends ComponentBase<AppFormOwnProps<FormData>, AppFormStateProps> {
  protected submitHandler = this.submitFunction.bind(this);

  // eslint-disable-next-line camelcase
  public async UNSAFE_componentWillUpdate(props: any) {
    const hasJustSubmittedSuccessfully = props.submittedSuccessfully && !this.props.submittedSuccessfully;
    await super.UNSAFE_componentWillUpdate(props);

    if (hasJustSubmittedSuccessfully && this.props.onSuccess) {
      this.props.onSuccess();
    }
  }

  protected loggedInRender() {
    return this.formRender();
  }

  protected loggedOutRender() {
    return this.formRender();
  }

  protected displayError() {
    return this.formRender();
  }

  protected formRender() {
    if (this.props.submitting) {
      return (
        <div className={styles.loading}>
          <HashLoader />
        </div>
      );
    }

    if (this.props.redirectToPathAfterSubmit !== undefined && this.props.submittedSuccessfully) {
      return <Redirect to={this.props.redirectToPathAfterSubmit} />;
    }

    return (
      <Form
        className={`${styles.appForm} form-group ${this.props.className}`}
        // @ts-ignore
        onSubmit={this.props.handleSubmit(this.submitHandler)}
      >
        {this.renderErrors()}
        {this.shouldDisplaySuccessMessage() ? this.props.successMessage : this.props.children}
        {!this.props.hideButtons && (
          <div className={styles.buttons}>
            <button className="btn btn-primary" type="submit" disabled={this.props.disableSubmit}>
              {this.props.submitText}
            </button>
            {this.props.onCancel && (
              <button className="btn btn-danger" type="button" onClick={this.props.onCancel}>
                {this.props.cancelText || 'Cancel'}
              </button>
            )}
          </div>
        )}
      </Form>
    );
  }

  protected shouldDisplaySuccessMessage(): boolean {
    return this.props.submittedSuccessfully && this.isSuccessMessageSet();
  }

  protected isSuccessMessageSet(): boolean {
    return !!this.props.successMessage;
  }

  protected submitFunction(data: FormData, dispatch: Dispatch, props: AppFormOwnProps<FormData>) {
    // eslint-disable-next-line no-restricted-syntax
    for (const field in data) {
      if (data[field] === '') {
        data[field] = null;
      }
    }

    if (this.props.preprocessDataOnSubmit) {
      try {
        data = this.props.preprocessDataOnSubmit(data);
      } catch (e) {
        throw new SubmissionError({
          _error: e.message,
        });
      }
    }

    dispatch(startSubmit(this.props.form));
    dispatch(createFormSubmitAction(data, this, dispatch, props));
  }

  protected renderErrors() {
    if (!this.props.submitFailed) {
      return null;
    }

    return (
      <span className={styles.error}>
        {this.props.error}
        {/* eslint-disable-next-line no-underscore-dangle */}
        {this.props.submitErrors && this.props.submitErrors._message}
      </span>
    );
  }
}

export function appFormMapStateToProps(
  state: ClippingPlatformState,
  ownProps: AppFormOwnProps<any>,
): AppFormStateProps {
  const formData = state.form[ownProps.form];

  return {
    ...baseMapStateToProps(state),
    error: formData && (formData as any).error,
    submitErrors: formData && formData.submitErrors,
    submitFailed: formData && formData.submitFailed,
    submittedSuccessfully: FormHelper.didSubmitSuccessfully(formData),
    submitting: FormHelper.isFormSubmitting(formData),
  };
}

export function appFormMapDispatchToProps(dispatch: Dispatch, ownProps: AppFormOwnProps<any>): AppFormDispatchProps {
  return {
    ...baseMapDispatchToProps(dispatch),
    clearFormErrors: () => dispatch(clearSubmitErrors(ownProps.form)),
  };
}

export default connect(
  appFormMapStateToProps,
  appFormMapDispatchToProps,
)(
  reduxForm<any, any>({
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
  })(AppForm),
);
